<template>
  <RootSystemGate @access="allowed = $event">
    <section class="operation-tool">
      <p v-if="!allowed" role="alert" class="text-danger">
        ไม่มีสิทธิ์ใช้งานส่วนนี้ อนุญาตเฉพาะผู้ดูแลระบบที่มี Role Type เป็น root
        เท่านั้น
      </p>
      <template v-else>
        <div class="tool-card template-grid">
          <label for="operation-template"
            >เทมเพลตคำสั่ง *
            <select
              id="operation-template"
              v-model="templateId"
              :disabled="locked"
              @change="selectTemplate"
            >
              <option v-for="item in templates" :key="item.id" :value="item.id">
                {{ item.label }}
              </option>
            </select>
          </label>
          <label for="operation-type"
            >ประเภท Operation
            <input id="operation-type" :value="operationType" readonly />
          </label>
        </div>
        <div class="editor-grid">
          <div class="tool-card">
            <label for="operation-command">คำสั่ง GraphQL *</label
            ><textarea
              id="operation-command"
              v-model="command"
              :disabled="locked"
              spellcheck="false"
              aria-label="คำสั่ง GraphQL"
            />
          </div>
          <div class="tool-card">
            <label for="operation-variables">ตัวแปร (JSON) *</label
            ><textarea
              id="operation-variables"
              v-model="variablesText"
              :disabled="locked"
              spellcheck="false"
              aria-label="ตัวแปร JSON"
            />
          </div>
        </div>
        <p class="tool-hint">{{ templateDescription }}</p>
        <p v-if="errorMessage" role="alert" class="text-danger tool-error">
          {{ errorMessage }}
        </p>
        <div class="tool-actions">
          <vs-button type="border" :disabled="locked" @click="selectTemplate"
            >คืนค่าเทมเพลต</vs-button
          ><vs-button
            icon-pack="feather"
            icon="icon-play"
            :disabled="locked"
            @click="run"
            >{{ busy ? 'กำลังดำเนินการ…' : 'เรียกใช้งาน' }}</vs-button
          >
        </div>
        <div v-if="resultText" class="tool-card result-card">
          <label for="operation-result">ผลลัพธ์ (JSON)</label
          ><textarea
            id="operation-result"
            :value="resultText"
            readonly
            spellcheck="false"
            aria-label="ผลลัพธ์ JSON"
          />
        </div>
        <vs-popup title="ยืนยันการดำเนินการ" :active.sync="confirmOpen">
          <template v-if="pending">
            <p>
              {{
                pending.templateId === 'dailyCalculators'
                  ? 'คำสั่งนี้เริ่มคำนวณดอกเบี้ยรายวันและเขียนข้อมูลในระบบ'
                  : pending.templateId === 'getPendingDailyCalculations'
                  ? 'ส่งอีเมลรายการค้างทั้งหมดไปยังผู้รับเดิม รวมถึงแจ้งเมื่อไม่พบรายการค้าง โดยไม่สั่งคำนวณดอกเบี้ย'
                  : pending.templateId === 'randomDraftApprovedContracts'
                  ? `สุ่ม ${pending.variables.count} สัญญา: Draft จะลบ Transaction และรีเซ็ตยอด แล้ว Approved กลับ ไม่คำนวณใหม่อัตโนมัติ ข้อมูลธุรกิจ rollback ทั้งชุดเมื่อผิดพลาด ส่วน log คงอยู่ ใช้เฉพาะ local dev`
                  : pending.templateId === 'randomRollbackDailyCalculations'
                  ? `สุ่ม ${pending.variables.count} สัญญา แล้วย้อนรายการคำนวณตั้งแต่ ${pending.variables.date} ด้วย flow เดียวกับปุ่มหน้าสัญญา ข้อมูลธุรกิจ rollback ทั้งชุดเมื่อผิดพลาด ส่วน log อาจคงอยู่ ใช้เฉพาะ local dev`
                  : 'คำสั่งนี้ล้างประวัติที่หมดอายุ การลบกู้คืนไม่ได้หากไม่มีข้อมูลสำรอง'
              }}
            </p>
            <p class="my-4">
              การปิดหน้านี้ไม่ยกเลิกงานบน Backend กรุณารอผลและอย่าส่งคำสั่งซ้ำ
            </p>
            <div v-if="previewReport" class="cleanup-preview">
              <p>ผลตรวจสอบก่อนลบ (จำนวนจริงอาจเปลี่ยนได้):</p>
              <ul>
                <li v-for="item in previewReport.results" :key="item.name">
                  {{ item.name }}:
                  {{ item.enabled ? item.eligible : 0 }} รายการ<template
                    v-if="item.name === 'CalculationLog'"
                  >
                    + รายละเอียด {{ item.detailEligible }} รายการ;
                    เว้นที่ถูกอ้างอิง {{ item.skipped }}</template
                  >
                </li>
              </ul>
            </div>
            <p class="my-4">พิมพ์ {{ confirmationCode }} เพื่อยืนยัน</p>
            <vs-input v-model="confirmation" class="w-full" :disabled="busy" />
            <vs-button
              class="mt-4"
              color="danger"
              :disabled="busy || confirmation !== confirmationCode"
              @click="confirmRun"
              >ยืนยันเรียกใช้งาน</vs-button
            >
            <vs-button
              class="mt-4 ml-2"
              type="border"
              :disabled="busy"
              @click="cancel"
              >ยกเลิก</vs-button
            >
          </template>
        </vs-popup>
      </template>
    </section>
  </RootSystemGate>
</template>

<script>
import { parse } from 'graphql'
import { apolloClient } from '@/setup/apollo-setup'
import { operationTemplates, prepareOperation } from './templates'
import RootSystemGate from '../RootSystemGate.vue'

export default {
  components: { RootSystemGate },
  data() {
    return {
      allowed: false,
      templates: operationTemplates.filter(
        (template) =>
          !template.developmentOnly || process.env.NODE_ENV === 'development'
      ),
      templateId: operationTemplates[0].id,
      command: operationTemplates[0].command,
      variablesText: operationTemplates[0].variables,
      busy: false,
      confirmOpen: false,
      pending: null,
      confirmation: '',
      previewReport: null,
      resultText: '',
      errorMessage: '',
    }
  },
  computed: {
    templateDescription() {
      const template = this.templates.find(
        (item) => item.id === this.templateId
      )
      return template ? template.description : ''
    },
    operationType() {
      return 'Mutation'
    },
    locked() {
      return this.busy || this.confirmOpen
    },
    confirmationCode() {
      if (
        this.pending &&
        this.pending.templateId === 'randomDraftApprovedContracts'
      )
        return 'RANDOM_DRAFT_APPROVED'
      if (
        this.pending &&
        this.pending.templateId === 'randomRollbackDailyCalculations'
      )
        return 'RANDOM_ROLLBACK_DAILY_CALCULATIONS'
      if (
        this.pending &&
        this.pending.templateId === 'getPendingDailyCalculations'
      )
        return 'SEND_PENDING_EMAIL'
      return this.pending && this.pending.templateId === 'clearDatabaseLogs'
        ? 'CLEAR_DATABASE_LOGS'
        : 'RUN_DAILY_CALCULATORS'
    },
  },
  methods: {
    selectTemplate() {
      if (this.locked) return
      const template = this.templates.find(
        (item) => item.id === this.templateId
      )
      this.command = template.command
      this.variablesText = template.variables
      this.resultText = ''
      this.errorMessage = ''
      this.cancel()
    },
    cancel() {
      if (!this.busy) {
        this.confirmOpen = false
        this.pending = null
        this.previewReport = null
        this.confirmation = ''
      }
    },
    async run() {
      if (!this.allowed || this.locked) return
      this.errorMessage = ''
      this.resultText = ''
      this.pending = null
      this.previewReport = null
      this.confirmation = ''
      let request
      try {
        request = prepareOperation(
          this.templateId,
          this.command,
          this.variablesText
        )
      } catch (error) {
        this.errorMessage = error.message
        return
      }
      if (
        request.templateId === 'getPendingDailyCalculations' &&
        !request.variables.sendEmail
      ) {
        await this.send(request)
        return
      }
      if (
        request.templateId === 'clearDatabaseLogs' &&
        request.variables.dryRun
      ) {
        await this.send(request)
        return
      }
      if (request.templateId === 'clearDatabaseLogs') {
        // Preview uses the full trusted template even if the user edits response selections.
        const cleanupTemplate = operationTemplates.find(
          (template) => template.id === 'clearDatabaseLogs'
        )
        const preview = await this.send({
          templateId: request.templateId,
          document: parse(cleanupTemplate.command),
          variables: { dryRun: true, confirmation: null },
        })
        if (
          !preview ||
          !preview.clearDatabaseLogs ||
          !preview.clearDatabaseLogs.completed
        ) {
          this.errorMessage =
            this.errorMessage ||
            'ตรวจสอบข้อมูลไม่ครบ ไม่อนุญาตให้ลบ กรุณาตรวจสอบ log'
          return
        }
        this.previewReport = preview.clearDatabaseLogs
      }
      this.pending = request
      this.confirmOpen = true
    },
    async confirmRun() {
      if (
        !this.allowed ||
        this.busy ||
        !this.confirmOpen ||
        !this.pending ||
        this.confirmation !== this.confirmationCode
      )
        return
      const request = this.pending
      if (request.templateId === 'randomDraftApprovedContracts')
        request.variables = {
          ...request.variables,
          confirmation: 'RANDOM_DRAFT_APPROVED',
        }
      if (request.templateId === 'randomRollbackDailyCalculations')
        request.variables = {
          ...request.variables,
          confirmation: 'RANDOM_ROLLBACK_DAILY_CALCULATIONS',
        }
      if (request.templateId === 'clearDatabaseLogs')
        request.variables = {
          ...request.variables,
          confirmation: 'CLEAR_DATABASE_LOGS',
        }
      await this.send(request)
      this.confirmOpen = false
      this.pending = null
      this.confirmation = ''
    },
    async send(request) {
      if (!this.allowed) {
        this.errorMessage =
          'ไม่มีสิทธิ์ใช้งานส่วนนี้ อนุญาตเฉพาะผู้ดูแลระบบที่มี Role Type เป็น root เท่านั้น'
        return null
      }
      this.busy = true
      try {
        const response =
          request.operation === 'query'
            ? await apolloClient.query({
                query: request.document,
                variables: request.variables,
                fetchPolicy: 'no-cache',
              })
            : await apolloClient.mutate({
                mutation: request.document,
                variables: request.variables,
                fetchPolicy: 'no-cache',
              })
        this.resultText = JSON.stringify(response.data, null, 2)
        return response.data
      } catch (error) {
        const batchError = (error.graphQLErrors || []).find(
          (item) =>
            item.extensions &&
            [
              'DEV_STATUS_BATCH_FAILED',
              'DEV_CALCULATION_ROLLBACK_FAILED',
            ].includes(item.extensions.code)
        )
        if (
          [
            'randomDraftApprovedContracts',
            'randomRollbackDailyCalculations',
          ].includes(request.templateId) &&
          batchError
        ) {
          this.errorMessage = batchError.message
          return null
        }
        if (
          (error.graphQLErrors || []).some((item) =>
            item.message.includes('Role Type')
          )
        ) {
          this.allowed = false
          this.errorMessage =
            'ไม่มีสิทธิ์ใช้งานส่วนนี้ อนุญาตเฉพาะผู้ดูแลระบบที่มี Role Type เป็น root เท่านั้น'
          return null
        }
        this.errorMessage =
          request.operation === 'query' ||
          (request.templateId === 'getPendingDailyCalculations' &&
            !request.variables.sendEmail) ||
          (request.templateId === 'clearDatabaseLogs' &&
            request.variables.dryRun)
            ? 'ตรวจสอบไม่ได้ กรุณาตรวจสิทธิ์ การเชื่อมต่อ และ log ของ Backend'
            : 'ยังยืนยันผลไม่ได้ งานบน Backend อาจยังทำต่อ กรุณาตรวจสอบ log ก่อนเรียกใหม่'
        return null
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.operation-tool {
  max-width: 1600px;
  margin: 0 auto;
}
.tool-description {
  color: #777583;
  margin: 0 0 28px;
  font-size: 16px;
}
.tool-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 5px 22px rgba(34, 41, 47, 0.08);
  padding: 28px;
}
.template-grid,
.editor-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 28px;
}
.editor-grid {
  margin-top: 28px;
}
.tool-card label {
  display: block;
  color: #777583;
  font-weight: 600;
  margin-bottom: 14px;
}
.template-grid label {
  margin-bottom: 0;
}
.tool-card input,
.tool-card select,
.tool-card textarea {
  width: 100%;
  border: 1px solid #d8d6de;
  border-radius: 7px;
  padding: 13px 16px;
  color: #514f60;
  background: #fff;
  font: inherit;
}
.template-grid input,
.template-grid select {
  display: block;
  margin-top: 14px;
  height: 48px;
}
.tool-card textarea {
  min-height: 430px;
  resize: vertical;
  font: 14px/1.65 ui-monospace, SFMono-Regular, Consolas, monospace;
}
.tool-card input:focus,
.tool-card select:focus,
.tool-card textarea:focus {
  outline: 2px solid #7367f0;
  outline-offset: -1px;
}
.tool-card :disabled {
  opacity: 0.65;
}
.tool-hint {
  color: #777583;
  margin: 18px 0;
}
.tool-error {
  white-space: pre-wrap;
  margin-bottom: 18px;
}
.tool-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.result-card {
  margin-top: 28px;
}
.result-card textarea {
  min-height: 230px;
}
.cleanup-preview {
  margin-top: 16px;
  overflow-wrap: anywhere;
}
.cleanup-preview ul {
  padding-left: 20px;
}
@media (max-width: 767px) {
  .template-grid,
  .editor-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
  .tool-card {
    padding: 20px;
  }
  .tool-card textarea {
    min-height: 240px;
  }
}
</style>
